const extractCode = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  if (!('code' in data)) return undefined;
  return typeof data.code === 'string' ? data.code : undefined;
};

const extractMessages = (data: unknown): string[] | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  if (!('messages' in data)) return undefined;
  const m = data.messages;
  if (!Array.isArray(m)) return undefined;
  if (!m.every((x): x is string => typeof x === 'string')) return undefined;
  return m;
};

/**
 * Convert a non-2xx response body into the normalized error shape
 * (`{ statusCode, code, messages, cause }`).
 * Each field (`code`, `messages`) is validated independently so a malformed
 * server response (e.g. `messages: null`) cannot poison the result.
 *
 * If neither field is recognizable, the raw body is preserved on `cause`
 * so callers and telemetry can still inspect it.
 */
export const normalizeResponseError = (
  status: number | undefined,
  data: unknown
): {
  statusCode: number | undefined;
  code: string | undefined;
  messages: string[] | undefined;
  cause: unknown;
} => {
  const code = extractCode(data);
  const messages = extractMessages(data);
  const recognized = code !== undefined || messages !== undefined;
  return {
    statusCode: status,
    code,
    messages,
    cause: recognized ? undefined : data,
  };
};

/**
 * Convert a rejected Promise (network failure, timeout, etc.) into the
 * normalized error shape (`{ statusCode, code, messages, cause }`).
 * The original error is preserved on `cause` so a telemetry sink can still see it.
 */
export const normalizeThrownError = (
  error: unknown
): {
  statusCode: number | undefined;
  code: string | undefined;
  messages: string[] | undefined;
  cause: unknown;
} => ({
  statusCode: undefined,
  code: undefined,
  messages: error instanceof Error ? [error.message] : undefined,
  cause: error,
});
